import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Button,
} from '@pantry/ui';
import {
  type ItemUnit,
  UNIT_DISPLAY_LABELS,
  getRelatedUnits,
  getUnitCategory,
} from '@pantry/api-client';

interface UnitSelectorProps {
  /** Current selected unit */
  value: ItemUnit;
  /** Called when a unit is selected */
  onChange: (unit: ItemUnit) => void;
  /** The item's base/stored unit (determines which units are available) */
  baseUnit: ItemUnit;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Optional className for the trigger button */
  className?: string;
}

/**
 * Dropdown selector for switching between related units.
 * Shows all units that can be converted to/from the base unit.
 * Hidden for 'item' unit type (no conversion possible).
 */
export function UnitSelector({
  value,
  onChange,
  baseUnit,
  disabled = false,
  className,
}: UnitSelectorProps) {
  // Don't show selector for discrete items - no unit conversion possible
  const category = getUnitCategory(baseUnit);
  if (category === 'discrete') {
    return <span className="text-sm text-muted-foreground px-2">{UNIT_DISPLAY_LABELS[value]}</span>;
  }

  const relatedUnits = getRelatedUnits(baseUnit);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button variant="outline" size="sm" className={className} disabled={disabled}>
          {UNIT_DISPLAY_LABELS[value]}
          <ChevronDownIcon className="h-3 w-3 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center">
        {relatedUnits.map((unit) => (
          <DropdownMenuItem
            key={unit}
            onClick={() => onChange(unit)}
            className="flex items-center justify-between"
          >
            <span>{UNIT_DISPLAY_LABELS[unit]}</span>
            {value === unit && <CheckIcon className="h-4 w-4 ml-2" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
